import React, {
  ComponentProps,
  MouseEventHandler,
  useCallback,
  useState,
} from 'react';
import { isNaN } from 'lodash';
import { Grid } from '@mui/material';
import {
  asPercentageRounded,
  numberWithCommas,
  removeLineBreaks,
} from 'spw-shared/utils';
import { useDisableBulkEditButton } from 'spw-shared/hooks/use-disable-bulk-edit-button';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { ChildName, USFValue } from '../macro-single-page-summary/styles';
import WorksheetRow from '../../../../models/WorksheetRow';
import BulkEditDialog from '../program-summary-tabs/bulk-edit-dialog';
import { AvgUSFBulkEditButton } from './style';

const BULK_EDIT_DIALOG_SUBJECT = 'USF';

export const ChildUsfMixRow = ({
  row,
  percentage,
  total,
  isMacro,
  avgUsf,
  shouldHideAvgUSF,
  testingPrefix,
  shouldShowClientFacingName,
  isIndividualWorkspacesCard,
}: {
  row: WorksheetRow;
  percentage: number | string;
  total: number;
  isMacro: boolean;
  avgUsf: number;
  shouldHideAvgUSF?: boolean;
  testingPrefix: string;
  shouldShowClientFacingName: boolean;
  isIndividualWorkspacesCard?: boolean;
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  const [bulkEditDialogProps, setBulkEditDialogProps] = useState<
    Omit<ComponentProps<typeof BulkEditDialog>, 'clientFacingNamesAreVisible'>
  >();

  const handleBulkEditDialogClose = useCallback(() => {
    setBulkEditDialogProps(undefined);
  }, []);
  const disableBulkEditButton = useDisableBulkEditButton({
    row,
    subject: BULK_EDIT_DIALOG_SUBJECT,
  });

  const handleAvgUsfBulkEditButtonClick = ((e) => {
    setBulkEditDialogProps({
      projectSpaceType: row.projectSpaceType,
      subject: BULK_EDIT_DIALOG_SUBJECT,
      anchorEl: e.currentTarget,
      onClose: handleBulkEditDialogClose,
    });
  }) as MouseEventHandler;

  const usf = isNaN(row.usf) ? 0 : row.usf;

  return (
    <Grid container>
      <Grid item xs={6}>
        <Grid container>
          <Grid item xs={7}>
            <ChildName
              $disableEllipsis={isIndividualWorkspacesCard && !isMacro}
              style={{ marginLeft: '0', marginRight: '-20px' }}
              data-testid={`${testingPrefix}_name`}
            >
              <pre>
                {removeLineBreaks(
                  shouldShowClientFacingName &&
                    row.projectSpaceType.clientFacingName
                    ? row.projectSpaceType.clientFacingName
                    : row.spaceType.name
                )}
              </pre>
            </ChildName>
          </Grid>
          {isMacro && (
            <Grid item xs={5}>
              <USFValue>
                {usf ? numberWithCommas(Math.round(convertArea(row.usf))) : usf}{' '}
                {getUnit('area')}
              </USFValue>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Grid container>
          <Grid item xs={4}>
            <ChildName
              className="align-right"
              data-testid={`${testingPrefix}_percentage`}
            >
              {typeof percentage === 'number'
                ? asPercentageRounded(percentage, false)
                : 0}
            </ChildName>
          </Grid>
          <Grid
            item
            xs={4}
            style={{
              paddingRight: isMacro ? '' : '16px',
              height: '24px',
            }}
          >
            <ChildName
              className="align-right"
              data-testid={`${testingPrefix}_totalUSF`}
            >
              {total
                ? numberWithCommas(Math.round(convertArea(total)), false, true)
                : 0}
            </ChildName>
          </Grid>
          <Grid
            item
            xs={4}
            style={{
              borderLeft: !isMacro ? '1px solid #E0E0E0' : undefined,
              height: '24px',
            }}
          >
            {!shouldHideAvgUSF && (
              <AvgUSFBulkEditButton
                $disableHoverState={disableBulkEditButton}
                className={bulkEditDialogProps ? 'selected' : ''}
                onClick={
                  !disableBulkEditButton
                    ? handleAvgUsfBulkEditButtonClick
                    : undefined
                }
              >
                <ChildName
                  className="align-right"
                  data-testid={`${testingPrefix}_averageUSF`}
                >
                  {avgUsf
                    ? numberWithCommas(Math.round(convertArea(avgUsf)))
                    : 0}{' '}
                  {getUnit('area')}
                </ChildName>
              </AvgUSFBulkEditButton>
            )}
            {bulkEditDialogProps && (
              <BulkEditDialog
                {...bulkEditDialogProps}
                clientFacingNamesAreVisible={shouldShowClientFacingName}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
